
import React, { useState, useEffect } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Typography,
  Chip,
  Container,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Avatar,
  Stack,
  Link,
} from '@mui/material';
import {
  Calendar,
  User,
  CheckCircle,
  X,
  Clock,
  Hourglass,
  Ban,
  X as XIcon,
} from 'lucide-react';
import {
  getAllLeaveTrackers,
  getAllAbsenceTrackers,
  getAllConvertedAbsenceTrackers,
  getByTitle
} from '../../services/progressTrackerAPIs.js';
import PageHeader from '../../components/headers/Header.jsx';
import { IMAGE_BASE_URL } from '../../config.js';
import HiringReport from './Hiring.jsx';

// Supported entity types
const SUPPORTED_ENTITIES = ['leave', 'absence', 'converted', 'request'];

const ProgressTrackerPage = () => {
  const [selectedEntity, setSelectedEntity] = useState('leave');
  const [trackingData, setTrackingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState({});
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('ALL');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [monthFilter, setMonthFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');

  const fetchTrackingData = async (entityType) => {
    setLoading(true);
    try {
      let data = [];
      if (entityType === 'leave') {
        data = await getByTitle('Leave Request');
      } else if (entityType === 'absence') {
        data = await getByTitle('Absence');
      } else if (entityType === 'converted') {
        data = await getByTitle('Converted Absence');
      } else if (entityType === 'request') { // ← ADD THIS
        data = await getByTitle('Request'); // ← ADD THIS
      } else {
        console.warn('fetchTrackingData: unsupported entityType', entityType);
        data = [];
      }

      if (!Array.isArray(data)) data = [];

      setTrackingData(data);
      setFilteredData(data);
      calculateInsights(data);
    } catch (error) {
      console.error('Error fetching tracking ', error);
      setTrackingData([]);
      setFilteredData([]);
      setInsights({});
    } finally {
      setLoading(false);
    }
  };

  const calculateInsights = (data) => {
    if (!Array.isArray(data)) data = [];
    const total = data.length;
    const completed = data.filter(d => d?.status === 'COMPLETED').length;
    const waiting = data.filter(d => d?.status === 'WAITING_FOR_APPROVAL').length;
    const rejected = data.filter(d => d?.status === 'REJECTED').length;
    const cancelled = data.filter(d => d?.status === 'CANCELLED').length;
    const notStarted = data.filter(d => d?.status === 'NOT_STARTED').length;
    setInsights({
      total,
      completed,
      waiting,
      rejected,
      cancelled,
      notStarted,
      completedPercentage: total > 0 ? ((completed / total) * 100).toFixed(1) : 0,
      waitingPercentage: total > 0 ? ((waiting / total) * 100).toFixed(1) : 0,
      rejectedPercentage: total > 0 ? ((rejected / total) * 100).toFixed(1) : 0,
      cancelledPercentage: total > 0 ? ((cancelled / total) * 100).toFixed(1) : 0,
    });
  };

  // When selectedEntity changes: reset filters/pagination and fetch only for supported entities
  useEffect(() => {
    console.log('🔄 selectedEntity changed to:', selectedEntity);
    setSelectedStatusFilter('ALL');
    setPage(0);

    if (!SUPPORTED_ENTITIES.includes(selectedEntity)) {
      setTrackingData([]);
      setFilteredData([]);
      setInsights({});
      setLoading(false);
      return;
    }

    fetchTrackingData(selectedEntity);
  }, [selectedEntity]);
  // Filtering / sorting effect — guarded for supported entities
  // Filtering / sorting effect — now supports 'request'
  useEffect(() => {
    if (!SUPPORTED_ENTITIES.includes(selectedEntity)) {
      setFilteredData([]);
      return;
    }

    let result = Array.isArray(trackingData) ? [...trackingData] : [];

    // Status filter
    if (selectedStatusFilter !== 'ALL') {
      result = result.filter((item) => item?.status === selectedStatusFilter);
    }

    // Employee search
    if (employeeSearch) {
      const q = employeeSearch.toLowerCase();
      result = result.filter((item) => {
        let employeeName = '';
        if (selectedEntity === 'leave') {
          employeeName = item?.leaveRequest?.employee?.fullName || '';
        } else if (selectedEntity === 'absence') {
          const absence = item?.absence || item?.stagedAbsence;
          employeeName = absence?.employee?.fullName || '';
        } else if (selectedEntity === 'converted') {
          employeeName =
            item?.absence?.employee?.fullName ||
            item?.leaveRequest?.employee?.fullName ||
            '';
        } else if (selectedEntity === 'request') {
          employeeName = item?.request?.employee?.fullName || '';
        }
        return employeeName.toLowerCase().includes(q);
      });
    }

    // Date range filter
    if (dateRange.start || dateRange.end) {
      const startLimit = dateRange.start ? new Date(dateRange.start) : null;
      const endLimit = dateRange.end ? new Date(dateRange.end) : null;
      result = result.filter((item) => {
        let dateToCompare = null;
        if (selectedEntity === 'leave') {
          dateToCompare = item?.leaveRequest?.startDate
            ? new Date(item.leaveRequest.startDate)
            : null;
        } else if (selectedEntity === 'absence') {
          const absence = item?.absence || item?.stagedAbsence;
          dateToCompare = absence?.date ? new Date(absence.date) : null;
        } else if (selectedEntity === 'converted') {
          dateToCompare = item?.absence?.date ? new Date(item.absence.date) : null;
        } else if (selectedEntity === 'request') {
          dateToCompare = item?.request?.createdAt
            ? new Date(item.request.createdAt)
            : null;
        }

        if (!dateToCompare || isNaN(dateToCompare)) return false;
        if (startLimit && dateToCompare < startLimit) return false;
        if (endLimit && dateToCompare > endLimit) return false;
        return true;
      });
    }

    // Month filter
    if (monthFilter) {
      const [year, month] = monthFilter.split('-').map((v) => parseInt(v, 10));
      if (!isNaN(year) && !isNaN(month)) {
        result = result.filter((item) => {
          let dateToCompare = null;
          if (selectedEntity === 'leave') {
            dateToCompare = item?.leaveRequest?.startDate
              ? new Date(item.leaveRequest.startDate)
              : null;
          } else if (selectedEntity === 'absence') {
            const absence = item?.absence || item?.stagedAbsence;
            dateToCompare = absence?.date ? new Date(absence.date) : null;
          } else if (selectedEntity === 'converted') {
            dateToCompare = item?.absence?.date ? new Date(item.absence.date) : null;
          } else if (selectedEntity === 'request') {
            dateToCompare = item?.request?.createdAt
              ? new Date(item.request.createdAt)
              : null;
          }

          if (!dateToCompare || isNaN(dateToCompare)) return false;
          return (
            dateToCompare.getFullYear() === year &&
            dateToCompare.getMonth() + 1 === month
          );
        });
      }
    }

    // Sorting
    result.sort((a, b) => {
      let valueA, valueB;

      if (orderBy === 'date') {
        const toDate = (rec) => {
          if (!rec) return new Date(0);
          if (selectedEntity === 'leave')
            return rec.leaveRequest?.startDate
              ? new Date(rec.leaveRequest.startDate)
              : new Date(0);
          if (selectedEntity === 'absence') {
            const absence = rec.absence || rec.stagedAbsence;
            return absence?.date ? new Date(absence.date) : new Date(0);
          }
          if (selectedEntity === 'converted')
            return rec.absence?.date ? new Date(rec.absence.date) : new Date(0);
          if (selectedEntity === 'request')
            return rec.request?.createdAt
              ? new Date(rec.request.createdAt)
              : new Date(0);
          return new Date(0);
        };
        valueA = toDate(a);
        valueB = toDate(b);
      } else if (orderBy === 'employee') {
        const toName = (rec) => {
          if (!rec) return '';
          if (selectedEntity === 'leave')
            return rec.leaveRequest?.employee?.fullName || '';
          if (selectedEntity === 'absence') {
            const absence = rec.absence || rec.stagedAbsence;
            return absence?.employee?.fullName || '';
          }
          if (selectedEntity === 'converted')
            return (
              rec.absence?.employee?.fullName ||
              rec.leaveRequest?.employee?.fullName ||
              ''
            );
          if (selectedEntity === 'request')
            return rec.request?.employee?.fullName || '';
          return '';
        };
        valueA = (toName(a) || '').toLowerCase();
        valueB = (toName(b) || '').toLowerCase();
      } else if (orderBy === 'status') {
        valueA = (a?.status || '').toLowerCase();
        valueB = (b?.status || '').toLowerCase();
      } else if (orderBy === 'title') {
        valueA = (a?.title || '').toLowerCase();
        valueB = (b?.title || '').toLowerCase();
      } else {
        return 0;
      }

      if (order === 'desc') return valueA < valueB ? 1 : -1;
      return valueA > valueB ? 1 : -1;
    });

    setFilteredData(result);
  }, [
    selectedStatusFilter,
    trackingData,
    selectedEntity,
    employeeSearch,
    dateRange,
    monthFilter,
    orderBy,
    order,
  ]);

  const handleEntityChange = (event, newEntity) => {
    console.log('🔘 Toggle clicked, newEntity:', newEntity);
    if (newEntity !== null) {
      setSelectedEntity(newEntity);
    }
  };

  const getStatusConfig = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED':
        return { color: '#10b981', bgColor: '#d1fae5', icon: CheckCircle, label: 'Completed' };
      case 'WAITING_FOR_APPROVAL':
        return { color: '#f59e0b', bgColor: '#fef3c7', icon: Clock, label: 'Waiting Approval' };
      case 'IN_PROGRESS':
        return { color: '#447aefff', bgColor: '#e2edfeff', icon: Clock, label: 'In progress' };
      case 'REJECTED':
        return { color: '#ef4444', bgColor: '#fee2e2', icon: X, label: 'Rejected' };
      case 'FAILED':
        return { color: '#ef4444', bgColor: '#fee2e2', icon: Ban, label: 'Failed' };
      case 'CANCELLED':
        return { color: '#6b7280', bgColor: '#f3f4f6', icon: Ban, label: 'Cancelled' };
      case 'NOT_STARTED':
        return { color: '#8b5cf6', bgColor: '#ede9fe', icon: Hourglass, label: 'Not Started' };
      default:
        return { color: '#6b7280', bgColor: '#f3f4f6', icon: Hourglass, label: 'Unknown' };
    }
  };

  const getLeaveTypeColor = (type) => {
    switch (type) {
      case 'ANNUAL_LEAVE': return '#3b82f6';
      case 'MATERNITY_LEAVE': return '#ec4899';
      case 'UNPAID_LEAVE': return '#f97316';
      default: return '#6b7280';
    }
  };

  const formatLeaveType = (type) => {
    return type?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const formatAbsenceType = (ty_pe) => {
    return ty_pe?.toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const clearFilters = () => {
    setEmployeeSearch('');
    setDateRange({ start: '', end: '' });
    setMonthFilter('');
    setSelectedStatusFilter('ALL');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleCardClick = (field, value) => {
    if (field === 'employee') {
      setEmployeeSearch(value);
    } else if (field === 'status') {
      setSelectedStatusFilter(value);
    } else if (field === 'month') {
      setMonthFilter(value);
    }
  };

  const visibleRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );


  const showEndDate = SUPPORTED_ENTITIES.includes(selectedEntity);

  return (
    <Box sx={{ minHeight: '100vh', py: 1 }}>
      <Container maxWidth="xl">
        <PageHeader
          title={'Progress Tracker Dashboard'}
          subtitle={'Monitor and track all actions and their processing status'}
        />

        {/* Filters Section */}
        <Paper sx={{ p: 3, mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
              <TextField
                label="Search Employee"
                variant="outlined"
                size="small"
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <User size={18} />
                    </InputAdornment>
                  ),
                  endAdornment: employeeSearch && (
                    <IconButton onClick={() => setEmployeeSearch('')} size="small">
                      <XIcon size={16} />
                    </IconButton>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              <TextField
                label="Select Month"
                type="month"
                variant="outlined"
                size="small"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                sx={{ minWidth: 180 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Start Date"
                type="date"
                variant="outlined"
                size="small"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                sx={{ minWidth: 150 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                variant="outlined"
                size="small"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                sx={{ minWidth: 150 }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<XIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatusFilter}
                  label="Status"
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All Statuses</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="WAITING_FOR_APPROVAL">Pending</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="NOT_STARTED">Not Started</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Paper>

        {/* Entity Toggle */}

        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={selectedEntity}
            exclusive
            onChange={handleEntityChange}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              '& .MuiToggleButton-root': {
                px: 3,
                py: 1.5,
                border: 'none',
                borderRadius: '5px !important',
                textTransform: 'none',
                fontWeight: 600,
                color: '#64748b',
                '&.Mui-selected': {
                  bgcolor: '#3b82f6',
                  color: 'white',
                  '&:hover': { bgcolor: '#2563eb' },
                },
              },
            }}
          >
            <ToggleButton value="leave">Leave Requests</ToggleButton>
            <ToggleButton value="absence">Absences</ToggleButton>
            <ToggleButton value="converted">Converted Absence</ToggleButton>
            <ToggleButton value="request">Other Requests</ToggleButton>
            {/* <ToggleButton value="offboarding">Offboarding</ToggleButton> */}
            <ToggleButton value="hiring">Hiring</ToggleButton>
            {/* <ToggleButton value="onboarding">Onboarding</ToggleButton> */}
            {/* <ToggleButton value="job">Job</ToggleButton> */}
            {/* <ToggleButton value="policies">Policies</ToggleButton> */}
            {/* <ToggleButton value="contract">Contracts</ToggleButton> */}
          </ToggleButtonGroup>
        </Box>
        {selectedEntity !== 'hiring' &&
          <>
            {/* Insights */}
            <Box
              sx={{
                width: '100%',
                mb: 4,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(5, 1fr)',
                },
                gap: 3,
              }}
            >
              <Paper onClick={() => handleCardClick('status', 'ALL')} sx={{ cursor: 'pointer', p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>Total Requests</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>{insights.total || 0}</Typography>
              </Paper>
              <Paper onClick={() => handleCardClick('status', 'COMPLETED')} sx={{ cursor: 'pointer', p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', bgcolor: '#d1fae5' }}>
                <Typography variant="body2" sx={{ color: '#047857', mb: 1 }}>Completed</Typography>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#047857' }}>{insights.completed || 0}</Typography>
                  <Typography variant="h4" sx={{ ml: 'auto', color: '#047857' }}>{insights.completedPercentage}%</Typography>
                </Box>
              </Paper>
              <Paper onClick={() => handleCardClick('status', 'WAITING_FOR_APPROVAL')} sx={{ cursor: 'pointer', p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', bgcolor: '#fef3c7' }}>
                <Typography variant="body2" sx={{ color: '#b45309', mb: 1 }}>Pending</Typography>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#b45309' }}>{insights.waiting || 0}</Typography>
                  <Typography variant="h4" sx={{ ml: 'auto', color: '#b45309' }}>{insights.waitingPercentage}%</Typography>
                </Box>
              </Paper>
              <Paper onClick={() => handleCardClick('status', 'REJECTED')} sx={{ cursor: 'pointer', p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', bgcolor: '#fee2e2' }}>
                <Typography variant="body2" sx={{ color: '#b91c1c', mb: 1 }}>Rejected</Typography>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#b91c1c' }}>{insights.rejected || 0}</Typography>
                  <Typography variant="h4" sx={{ ml: 'auto', color: '#b91c1c' }}>{insights.rejectedPercentage}%</Typography>
                </Box>
              </Paper>
              <Paper onClick={() => handleCardClick('status', 'CANCELLED')} sx={{ cursor: 'pointer', p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', bgcolor: '#f3f4f6' }}>
                <Typography variant="body2" sx={{ color: '#4b5563', mb: 1 }}>Cancelled</Typography>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#4b5563' }}>{insights.cancelled || 0}</Typography>
                  <Typography variant="h4" sx={{ ml: 'auto', color: '#4b5563' }}>{insights.cancelledPercentage}%</Typography>
                </Box>
              </Paper>
            </Box>
          </>
        }
        {/* Table Section */}
        <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {selectedEntity !== 'hiring' &&
            <>
              <TableContainer>
                <Table stickyHeader aria-label="progress tracker table">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'title'}
                          direction={orderBy === 'title' ? order : 'asc'}
                          onClick={() => handleRequestSort('title')}
                        >
                          Title
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'employee'}
                          direction={orderBy === 'employee' ? order : 'asc'}
                          onClick={() => handleRequestSort('employee')}
                        >
                          Employee
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'date'}
                          direction={orderBy === 'date' ? order : 'asc'}
                          onClick={() => handleRequestSort('date')}
                        >
                          Start Date
                        </TableSortLabel>
                      </TableCell>
                      {showEndDate && <TableCell>End Date</TableCell>}
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'status'}
                          direction={orderBy === 'status' ? order : 'asc'}
                          onClick={() => handleRequestSort('status')}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Handled By</TableCell>
                      <TableCell>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={showEndDate ? 8 : 7} align="center" sx={{ py: 5 }}>
                          <Typography variant="h6" color="textSecondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleRows.map((item) => {
                        const statusConfig = getStatusConfig(item?.status);
                        const StatusIcon = statusConfig.icon;

                        let record = {};
                        let startDate = null;
                        let endDate = null;
                        let typeLabel = 'Unknown';
                        let typeColor = '#6b7280';

                        if (selectedEntity === 'leave') {
                          record = item?.leaveRequest || {};
                          startDate = record?.startDate || null;
                          endDate = record?.endDate || null;
                          typeLabel = formatLeaveType(record?.type);
                          typeColor = getLeaveTypeColor(record?.type);
                        } else if (selectedEntity === 'absence') {
                          record = item?.absence || item?.stagedAbsence || {};
                          startDate = record?.date || null;
                          endDate = record?.endDate || null;
                          typeLabel = formatAbsenceType(record?.ty_pe);
                          typeColor = '#334155';
                        } else if (selectedEntity === 'converted') {
                          record = item?.absence || {};
                          startDate = item?.absence?.date || null;
                          endDate = item?.absence?.endDate || null;
                          typeLabel = `Leave: ${formatLeaveType(item?.leaveRequest?.type)} | Absence: ${formatAbsenceType(item?.absence?.ty_pe)}`;
                          typeColor = getLeaveTypeColor(item?.leaveRequest?.type);
                        } else if (selectedEntity === 'request') {
                          // ✅ NEW: handle ProgressTracker with embedded 'request'
                          record = item?.request || {};
                          startDate = record?.createdAt ? new Date(record.createdAt) : null; // LocalDate → may need to append time
                          endDate = null; // requests typically don’t have end dates
                          typeLabel = record?.type || 'Request';
                          // Optional: color by documentType
                          if (record?.documentType === 'CONTRACT') typeColor = '#8b5cf6';
                          else if (record?.documentType === 'BANK_DETAILS_FORM') typeColor = '#10b981';
                          else typeColor = '#6b7280';
                        }

                        const employee = record?.employee || {};

                        let progressValue = 0;
                        if (item?.status === 'COMPLETED') progressValue = 100;
                        else if (item?.status === 'WAITING_FOR_APPROVAL') progressValue = 50;
                        else if (item?.status === 'NOT_STARTED') progressValue = 0;
                        else progressValue = 25;

                        return (
                          <TableRow
                            key={item?.id || Math.random()}
                            hover
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                              '&:hover': { bgcolor: '#f8fafc' },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              <Link
                                href="#"
                                underline="hover"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCardClick('title', item?.title);
                                }}
                              >
                                <Typography variant="body2" fontWeight="600">
                                  {item?.title || '—'}
                                </Typography>
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar
                                  sx={{
                                    bgcolor: '#3b82f6',
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {employee?.image ? (
                                    <img
                                      src={`${IMAGE_BASE_URL}${employee.image}`}
                                      alt={employee?.fullName || 'User'}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    (employee?.fullName && employee.fullName.charAt(0)) || 'U'
                                  )}
                                </Avatar>
                                <Link
                                  href="#"
                                  underline="hover"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleCardClick('employee', employee?.fullName);
                                  }}
                                >
                                  <Typography variant="body2">
                                    {employee?.fullName || 'N/A'}
                                  </Typography>
                                </Link>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Calendar size={16} color="#64748b" />
                                <Typography variant="body2">
                                  {startDate ? new Date(startDate).toLocaleDateString() : 'N/A'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            {showEndDate && (
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Calendar size={16} color="#64748b" />
                                  <Typography variant="body2">
                                    {endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            )}
                            <TableCell>
                              <Link
                                href="#"
                                underline="hover"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCardClick('status', item?.status);
                                }}
                              >
                                <Chip
                                  icon={<StatusIcon size={16} />}
                                  label={statusConfig.label}
                                  sx={{
                                    bgcolor: statusConfig.bgColor,
                                    color: statusConfig.color,
                                    fontWeight: 600,
                                    border: `1px solid ${statusConfig.color}40`,
                                  }}
                                />
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={typeLabel}
                                size="small"
                                sx={{
                                  bgcolor: (selectedEntity === 'leave' || selectedEntity === 'converted')
                                    ? `${typeColor}15`
                                    : '#cbd5e1',
                                  color: (selectedEntity === 'leave' || selectedEntity === 'converted')
                                    ? typeColor
                                    : '#334155',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {record?.handledBy || record?.recordedBy || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <LinearProgress
                                variant="determinate"
                                value={progressValue}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: '#e2e8f0',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: statusConfig.color,
                                    borderRadius: 3,
                                  },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              /> </>
          }
          {selectedEntity === 'hiring' &&
            <HiringReport />

          }
        </Paper>

      </Container>
    </Box>
  );
};

export default ProgressTrackerPage;